import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { useLocation } from "react-router-dom";
import { ReduxState } from "../../../../../redux";
import { setDownloadOptionsSelectedDocumentIds } from "../../../../../redux/reducers/TransactionManager";
import { CheckboxOption } from "./utils";
import { getDocumentName } from "src/views/Pages/Analyze/AnalyzeTransactionsTable/utils";

export const DocumentsDropdown: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const { selectedDocumentId } = useSelector(
    (state: ReduxState) => state.TransactionManager
  );
  const { downloadOptions } = useSelector(
    (state: ReduxState) => state.TransactionManager
  );
  const { selectedDocumentIds } = downloadOptions;

  const { data: jobDocs } = useSelector(
    (state: ReduxState) => state.JobDocument
  );

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const newDocumentsParam = queryParams.get("newDocuments");
  const l1 = queryParams.get("l1");
  const l2 = queryParams.get("l2");

  const uniqueDocumentIds = Array.from(
    new Set((jobDocs || []).map((item) => item.documentId))
  );

  const options = uniqueDocumentIds.map((docId) => ({
    value: docId,
    label: getDocumentName(jobDocs || [], docId),
  }));

  // Stable key built from the current job's documents. Using length alone missed the case where
  // the user navigates from job A (1 doc) to job B (1 doc) — length stayed 1 so the effect
  // never re-ran, leaving the stale docA in `selectedDocumentIds`. On export, the client POSTed
  // docA to /job/B/export; the server saved a JobExports row under jobB with transactions from
  // docA — producing exports with `numTransactions` equal to N*(real count) when sibling jobs
  // also held duplicate uploads of the same PDF.
  const jobDocsKey = uniqueDocumentIds.map(String).sort().join(",");

  // Set default selection based on URL parameters
  useEffect(() => {
    if ((jobDocs || []).length > 0) {
      if (newDocumentsParam) {
        // If newDocuments param exists, always select those documents (default behavior)
        const newDocs = newDocumentsParam.split(",");
        dispatch(setDownloadOptionsSelectedDocumentIds(newDocs));
        return;
      }

      // If no newDocuments param, make sure the current selection still refers to this job's
      // documents. Anything that isn't in `uniqueDocumentIds` is stale (left over from a prior
      // job the user visited) and must be pruned before the download request goes out.
      const validSet = new Set(uniqueDocumentIds.map(String));
      const pruned = selectedDocumentIds.filter((id) => validSet.has(String(id)));
      if (pruned.length === 0) {
        // Stale or uninitialized — fall back to selecting all of *this* job's documents.
        dispatch(setDownloadOptionsSelectedDocumentIds(uniqueDocumentIds));
      } else if (pruned.length !== selectedDocumentIds.length) {
        dispatch(setDownloadOptionsSelectedDocumentIds(pruned));
      }
    }
  }, [newDocumentsParam, jobDocsKey]);

  const selectedOptions = options.filter((option) =>
    selectedDocumentIds.includes(option.value)
  );

  const handleSelectAll = () => {
    // Select all documents without any filtering
    dispatch(setDownloadOptionsSelectedDocumentIds(uniqueDocumentIds));
  };

  const handleSelectLatest = () => {
    if (newDocumentsParam) {
      // Select documents from the newDocuments query parameter
      const newDocs = newDocumentsParam.split(",");
      dispatch(setDownloadOptionsSelectedDocumentIds(newDocs));
    }
  };

  return (
    <div>
      <div className="mb-2 d-flex align-items-center">
        <div className="small text-muted mr-2">Select:</div>
        <div>
          {l1 && l2 && (
            <>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleSelectAll();
                }}
                className="text-primary mr-3"
              >
                All docs in project
              </a>
            </>
          )}
          {newDocumentsParam && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleSelectLatest();
              }}
              className="text-primary"
            >
              Latest docs
            </a>
          )}
        </div>
      </div>
      <div>
        <Select
          options={options}
          value={selectedOptions}
          onChange={(value) => {
            dispatch(
              setDownloadOptionsSelectedDocumentIds(
                ((value || []) as any).map((x) => x.value)
              )
            );
          }}
          isMulti={true}
          closeMenuOnSelect={false}
          placeholder="Select documents to download..."
          className="basic-multi-select"
          classNamePrefix="select"
          components={{
            Option: CheckboxOption,
          }}
          styles={{
            // Cap the height of the selected-chips area so a job with many
            // documents scrolls instead of pushing the rest of the modal down.
            valueContainer: (base) => ({
              ...base,
              maxHeight: 120,
              overflowY: "auto",
            }),
          }}
        />
      </div>
    </div>
  );
};
export default DocumentsDropdown;
